import React, { useContext, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Button, Checkbox, Flex, Steps, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { StepSubtitle } from 'src/pages/observers/AddObserverPage/styles';

import {
	useLazyGetObserverByIdQuery,
	useLazyGetObserverWhatsappGroupByIdQuery,
	usePostObserverMutation,
	usePostObserverWhatsappGroupMutation
} from 'src/redux/services/centralApi';
import {
	FieldRestriction,
	ICondition,
	IQueryFieldsItem,
	OBSERVER_REPORT_SOURCE,
	ReportBlock,
	RowRestriction
} from 'src/interfaces/entities/observer';
import type {
	IQueryResult
} from 'src/pages/observers/components/CodeEditor/types';
import {
	CodeEditor,
	PeriodicitySelect,
	PreviewMessageInput,
	QueryDisplay,
	RestrictionsArea,
	SeveritySelect,
	TagsSelect,
	WhatsAppGroupsSelect,
	SqlFilter,
	FormItemInput,
	ObserverTypeAlert,
	ReportBlocksEditor
} from 'src/pages/observers/components';
import ModuleSelect from 'src/pages/observers/components/ModuleSelect';
import { checkRole } from 'src/common/helpers/checkRole';
import { Field } from 'react-querybuilder';
import { getConditions } from 'src/pages/observers/components/CodeEditor/utils';
import _ from 'lodash';
import { AuthContext } from 'src/AuthProvider';

export default function AddObserverPage() {
	const { user, } = useContext(AuthContext);
	const [ searchParams, ] = useSearchParams();

	const navigate = useNavigate();

	const [
		postObserverWhatsappGroup,
		postObserverWhatsappGroupResult,
	] = usePostObserverWhatsappGroupMutation();
	const [ getObserverById, {
		data: observerData,
		isLoading: isLoadingObserver,
	},] = useLazyGetObserverByIdQuery();
	const [
		getObserverWhatsappGroupData,
		observerWhatsappGroupDataResult,
	] = useLazyGetObserverWhatsappGroupByIdQuery();
	const [ postObserver, postObserverResult, ] = usePostObserverMutation();

	const [ currentStep, setCurrentStep, ] = useState<number>(0);

	const [ sqlQuery, setSqlQuery, ] = useState<string>('');
	const [ queryResult, setQueryResult, ] = useState<IQueryResult>();
	const [ queryFields, setQueryFields, ] = useState<IQueryFieldsItem[]>([]);

	const [ name, setName, ] = useState<string>('');
	const [ tags, setTags, ] = useState<string[]>([]);
	const [ module, setModule, ] = useState<string | null>(null);
	const [ severity, setSeverity, ] = useState<string>();
	const [ shortName, setShortName, ] = useState<string>('');
	const [ isActive, setIsActive, ] = useState<boolean>(false);
	const [ periodicity, setPeriodicity, ] = useState<string>();
	const [ customPreviewMessage, setCustomPreviewMessage, ] = useState<string>('');
	const [ whatsappGroups, setWhatsappGroups, ] = useState<string[]>([]);
	const [ defaultWhatsappGroups, setDefaultWhatsappGroups, ] = useState<string[]>([]);

	const [ rowRestrictions, setRowRestrictions, ] = useState<RowRestriction[]>([]);
	const [ fieldRestrictions, setFieldRestrictions, ] = useState<FieldRestriction[]>([]);

	const [ conditions, setConditions, ] = useState<Record<string, ICondition>>({});
	const [ saveConditions, setSaveConditions, ] = useState<boolean>(true);
	const [ onlyNotify, setOnlyNotify, ] = useState<boolean>(false);
	const [ sendSqlResult, setSendSqlResult, ] = useState<boolean>(false);
	const [ reportBlocks, setReportBlocks, ] = useState<ReportBlock[]>([]);
	const previewData = useMemo(() => ({ [OBSERVER_REPORT_SOURCE]: queryResult?.data ?? [], }), [ queryResult, ]);

	const isSuperAdmin = useMemo(() => {
		return checkRole('super_admin', { user, });
	}, [ user, ]);

	useEffect(() => {
		const id = searchParams.get('id');
		if (!id) return;
		getObserverById({ id, });
		getObserverWhatsappGroupData({ id, });
	}, [ searchParams, ]);

	useEffect(() => {
		if (!isLoadingObserver && observerData && observerWhatsappGroupDataResult.data) {
			try {
				setSqlQuery(observerData.sql || '');
				setName(observerData.name || '');
				setShortName(observerData.short_name || '');
				setCustomPreviewMessage(observerData.custom_preview_message || '');
				setTags(Array.isArray(observerData.tags) ? observerData.tags : []);
				const myGroupsStringArray = observerWhatsappGroupDataResult.data
					.map(group => (
						group.group_id
					));
				setDefaultWhatsappGroups(myGroupsStringArray);
				setQueryFields(
					Array.isArray(observerData.sql_test_result) ? observerData.sql_test_result : []
				);
				setRowRestrictions(
					Array.isArray(observerData.row_restrictions) ? observerData.row_restrictions : []
				);
				setFieldRestrictions(
					Array.isArray(observerData.field_restrictions) ? observerData.field_restrictions : []
				);
				setModule(observerData.module_id || null);
				setSeverity(observerData.severity_id || undefined);
				setPeriodicity(observerData.periodicity_id || undefined);
				setConditions(observerData.conditions && typeof observerData.conditions === 'object' ? observerData.conditions : {});
				setIsActive(false);
				if (Array.isArray(observerData.flags)) {
					setOnlyNotify(observerData.flags.includes('only_notify'));
					setSendSqlResult(observerData.flags.includes('send_sql_result'));
				} else {
					setOnlyNotify(false);
					setSendSqlResult(false);
				}
				setReportBlocks(observerData.report?.blocks ?? []);
				if (observerData.sql_test_result && observerData.sql_test_result.length > 0) {
					setCurrentStep(2);
				}

				toast.success('Dados do observador carregados para duplicação!');
			} catch (error) {
				toast.error('Erro ao carregar dados para duplicação');
				console.error('Erro ao decodificar dados de duplicação:', error);
			}
		}
	}, [ observerData, observerWhatsappGroupDataResult, ]);

	useEffect(() => {
		const _conditions = getConditions(sqlQuery);

		const newFilters = Object.keys(_conditions).reduce((acc, _conditionName) => {
			const existingFilterName = Object.keys(conditions).find(conditionName =>
				conditionName === _conditionName
			);

			acc[_conditionName] = existingFilterName
				? {
					..._conditions[_conditionName],
					value: conditions[existingFilterName].value,
				}
				: { ..._conditions[_conditionName], };

			return acc;
		}, {} as Record<string, ICondition>);

		setConditions(newFilters);
	}, [ sqlQuery, ]);

	const handleNextButton = () => {
		const isSuperAndHaveModule = isSuperAdmin && !module;

		const isFirstStepOk = queryFields.length > 0;
		const isSecondStepOk = customPreviewMessage && name &&
			shortName && periodicity && severity;

		const isThirdStepOk = fieldRestrictions.length > 0 ||
			rowRestrictions.length > 0;
		switch (currentStep) {
			case 0:
				if (isFirstStepOk) {
					setCurrentStep(prev => prev + 1);
					return;
				}

				toast.error(
					'É necessário uma consulta com retorno'
					+ ' válido para continuar.'
				);
				break;
			case 1:
				if (isSecondStepOk) {
					if (isSuperAndHaveModule) {
						toast.error('Selecione um módulo para o observador.');
						return;
					}
					setCurrentStep(prev => prev + 1);
					return;
				}

				toast.error(
					'Preencha todos os campos marcados'
					+ ' com \'*\' para avançar.'
				);
				break;
			case 2:
				if (isFirstStepOk) {
					if (isSecondStepOk && !isSuperAndHaveModule) {
						if (isThirdStepOk) {
							handleCreateObserver();
						} else {
							toast.error(
								'É necessário pelo menos 1 condição de alerta.'
							);
						}
					} else {
						toast.error(
							'Preencha todos os campos marcados'
							+ ' com \'*\' para avançar.'
						);
					}
				}
				break;
		}
	};

	const handleCreateObserver = () => {
		const creation_date = dayjs().format('YYYY-MM-DD');
		const fixedConditions: Record<string, ICondition> = { ...conditions, };

		if (!saveConditions) {
			Object.keys(conditions).forEach(conditionName => {
				fixedConditions[conditionName].value = null;
			});
		}

		const flags: string[] = [];
		if (onlyNotify) {
			flags.push('only_notify');
		}
		if (sendSqlResult) {
			flags.push('send_sql_result');
		}

		postObserver({
			name,
			tags,
			creation_date,
			module_id: module,
			module_sql_url: null,
			sql_url: sqlQuery,
			is_active: isActive,
			short_name: shortName,
			severity_id: severity ?? '',
			periodicity_id: periodicity ?? '',
			sql_test_result: queryFields,
			row_restrictions: rowRestrictions,
			field_restrictions: fieldRestrictions,
			custom_preview_message: customPreviewMessage,
			conditions: fixedConditions,
			flags: flags,
			report: sendSqlResult && reportBlocks.length ? { blocks: reportBlocks, } : null,
		})
			.unwrap()
			.then((res) => {
				const observer_id = res.payload.observer.observer_id;

				whatsappGroups.forEach((group) => {
					postObserverWhatsappGroup({
						observer_id,
						group_id: group,
					});
				});

				navigate('/observador/listar');
			})
			.catch((err) => {
				if (err?.status && err?.status === 500 && err?.data?.frontend_message) {
					toast.error(err?.data?.frontend_message, {
						autoClose: 1000,
					});
					return;
				}
				// 400: erro de validação (ex.: bloco do relatório incompleto)
				toast.error(err?.data?.[0]?.msg ?? 'Ocorreu um erro inesperado!');
			});
	};

	const clearRestrictions = () => {
		setFieldRestrictions([]);
		setRowRestrictions([]);
	};

	return <DashboardTemplate pageTitle="Adicionar Observador">
		<ObserverTypeAlert current='observer' />
		<Steps
			direction="vertical"
			size="small"
			current={currentStep}
			items={[
				{
					title: 'Consulta SQL',
					description: <Flex vertical gap={16}>
						<StepSubtitle>
							Execute uma consulta SQL válida para continuar.
						</StepSubtitle>
						<hr />
						<div>
							<CodeEditor
								editor_props={{
									value: sqlQuery || '',
								}}
								executable
								height='513px'
								conditions={conditions}
								hasReturn={Boolean(queryResult)}
								onCodeChange={(value) => setSqlQuery(value)}
								onQueryResultChange={(value) =>
									setQueryResult(value)
								}
								onQueryFieldsChange={(value) => {
									if (value.length !== queryFields.length) {
										const normalizadeValue = value.map(obj => {
											_.fromPairs(_.sortBy(_.toPairs(obj), 0));
										});
										const normalizadeQueryFields = value.map(obj => {
											_.fromPairs(_.sortBy(_.toPairs(obj), 0));
										});
										const sortedValue = _.sortBy(normalizadeValue, 'field');
										const sortedQueryFields = _.sortBy(normalizadeQueryFields, 'field');

										if (_.isEqual(sortedValue, sortedQueryFields)) {
											const prevStep = currentStep;
											setCurrentStep(1);
											setTimeout(() => {
												clearRestrictions();
												setCurrentStep(prevStep);
											}, 60);
										}
									}
									setQueryFields(value);
								}}
							/>
							{
								(queryFields && queryResult) &&
								<QueryDisplay
									queryResult={queryResult}
									queryFields={queryFields}
								/>
							}

						</div>
						{
							Object.keys(conditions).map((conditionName, i) => {
								const condition = conditions[conditionName];
								const handleFilterChange = (
									updatedValue: string | number | boolean | null
								) => {
									const updatedFilter = {
										...condition,
										value: updatedValue,
									};

									setConditions(prev => ({
										...prev,
										[conditionName]: updatedFilter,
									}));
								};

								if (condition.type === 'sql') {
									const fields: Field[] = [];

									if (condition.columns) {
										condition.columns?.forEach(col =>
											fields.push(
												{
													label: col.name,
													name: col.name,
													inputType: col.type,
												}
											)
										);
									}

									return <SqlFilter
										required={condition.required}
										key={i}
										fields={fields}
										label={conditionName}
										onConditionChange={(value) =>
											handleFilterChange(value)
										}
									/>;
								}

								return <FormItemInput
									key={i}
									inputType={condition.type}
									required={condition.required}
									value={condition.value}
									tooltip='Filtros dinâmicos para serem inseridos como condição na consulta SQL.'
									label={conditionName}
									placeholder={conditionName}
									onInputChange={(value) => {
										if (typeof value === 'string') {
											if (condition.type === 'number') {
												const formattedValue = value.replace(/[^0-9., ]/g, '');
												handleFilterChange(formattedValue);
												return;
											} else if (condition.type === 'text') {
												const formattedValue = value;
												handleFilterChange(formattedValue);
											}
										}
										handleFilterChange(value);
									}}
								/>;
							})
						}
						{
							Object.keys(conditions).length > 0 && (
								<Checkbox
									checked={saveConditions}
									onChange={(e) =>
										setSaveConditions(e.target.checked)
									}
								>
									Salvar valor das condições como predefinição padrão para o observador.
								</Checkbox>
							)
						}
					</Flex>,
				},
				{
					title: 'Configurações do observador',
					disabled: currentStep < 1,
					description: <Flex vertical gap={12}>
						<StepSubtitle>
							Configure os detalhes do seu observador.
						</StepSubtitle>
						{
							currentStep >= 1 &&
							<>
								<hr />
								<PreviewMessageInput
									defaultMessage={customPreviewMessage}
									queryFields={queryFields}
									onMessageChange={(value) =>
										setCustomPreviewMessage(value)
									}
								/>
								<FormItemInput
									value={name}
									required
									label='Nome:'
									placeholder='Nome do observador'
									onInputChange={(value) => {
										if (typeof value === 'string')
											setName(value as string);
									}}
								/>
								<FormItemInput
									required
									value={shortName}
									label='Nome abreviado/simplificado:'
									placeholder='Nome do simplificado'
									onInputChange={(value) => {
										if (typeof value === 'string') {
											if (value.length > 30) {
												toast.warn('O nome abreviado deve conter no máximo 30 caracteres!');
											}
											setShortName(value.substring(0, 30));
										}
									}}
									tooltip='Nome para ser utilizado como variável do sistema durante a criação de dashboards personalizados. Para que o desenvolvimento de dashboards seja simplificado. (max: 30char)'
								/>
								<PeriodicitySelect
									defaultPeriodicity={periodicity}
									onPeriodicityChange={(value) =>
										setPeriodicity(value)
									}
								/>
								<SeveritySelect
									defaultSeverity={severity}
									onSeverityChange={(value) =>
										setSeverity(value)
									}
								/>
								<WhatsAppGroupsSelect
									defaultWhatsappGroup={defaultWhatsappGroups}
									onWhatsappGroupChange={(value) =>
										setWhatsappGroups(value)
									}
								/>
								<ModuleSelect
									defaultModule={module}
									onModuleChange={(value) =>
										setModule(value)
									}
								/>
								<TagsSelect
									defaultTags={tags}
									onTagsChange={(value) => setTags(value)}
								/>
								<hr />
								<Checkbox
									checked={isActive}
									onChange={(e) =>
										setIsActive(e.target.checked)
									}
								>
									Observador ativo
								</Checkbox>
								<Checkbox
									checked={onlyNotify}
									onChange={(e) =>
										setOnlyNotify(e.target.checked)
									}
								>
									Enviar notificação sem gerar ocorrência?
								</Checkbox>
								<Checkbox
									checked={sendSqlResult}
									onChange={(e) => {
										setSendSqlResult(e.target.checked);
										if (!e.target.checked) setReportBlocks([]);
									}}
								>
									Enviar relatório em anexo?{' '}
									<Tooltip title='Sem blocos, envia a tabela completa do resultado. Com blocos, envia os gráficos e tabelas escolhidos, com as linhas que dispararam o alerta.'>
										<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help', }} />
									</Tooltip>
								</Checkbox>
								{
									sendSqlResult && <ReportBlocksEditor
										sources={[{ key: OBSERVER_REPORT_SOURCE, fields: queryFields, },]}
										blocks={reportBlocks}
										onBlocksChange={setReportBlocks}
										preview={{ title: name || 'Observador', data: previewData, }}
									/>
								}
							</>
						}
					</Flex>,
				},
				{
					title: 'Condições de alerta',
					disabled: currentStep < 2,
					description: <>
						<Flex gap={16} vertical>
							<StepSubtitle>
								Configure quando será acionado uma
								ocorrência para este observador.
							</StepSubtitle>
							{
								currentStep >= 2 &&
								<>
									<hr />
									<RestrictionsArea
										disabled={currentStep < 2}
										queryFields={queryFields}
										onRestrictionsChange={(fields, rows) => {
											setFieldRestrictions(fields);
											setRowRestrictions(rows);
										}}
									/>
								</>
							}
						</Flex>
					</>,
				},
			]}
		/>
		<Flex justify='end'>
			<Button
				loading={
					postObserverResult.isLoading ||
					postObserverWhatsappGroupResult.isLoading
				}
				type='primary'
				onClick={handleNextButton}
			>
				{currentStep === 2 ? 'Salvar' : 'Avançar'}
			</Button>
		</Flex>
	</DashboardTemplate>;
}